/** TodoCommentThread.tsx — 댓글 스레드 (시간순, 첨부 가능, Ctrl+V/드래그/버튼) */
import { useState, useEffect, useRef, useCallback } from 'react';
import { C, T, R } from './ui';
import * as todosApi from '../api-todos';
import type { TodoComment } from '../types';

interface Props {
  todoId: string;
  myId: string;
  creatorId: string;
  onToast?: (msg: string, kind?: 'success' | 'error') => void;
  onCountChange?: (count: number) => void;
}

function fmtDateTime(s: string): string {
  const d = new Date(s);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${mi}`;
}

function fileSize(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}

export default function TodoCommentThread({ todoId, myId, creatorId, onToast, onCountChange }: Props) {
  const [comments, setComments] = useState<TodoComment[]>([]);
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // onCountChange를 ref로 유지 (부모 재렌더로 인한 무한 루프 방지)
  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => { onCountChangeRef.current = onCountChange; }, [onCountChange]);

  const reload = useCallback(async () => {
    try {
      const r = await todosApi.getComments(todoId);
      if (r.success && r.data) {
        setComments(r.data);
        onCountChangeRef.current?.(r.data.length);
      }
    } finally { setLoading(false); }
  }, [todoId]);

  useEffect(() => { reload(); }, [reload]);

  function addFiles(arr: File[]) {
    if (arr.length === 0) return;
    setFiles(prev => [...prev, ...arr]);
  }

  function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imgs: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) {
          const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
          const ext = (f.type.split('/')[1] || 'png').split('+')[0];
          imgs.push(new File([f], `paste-${ts}.${ext}`, { type: f.type }));
        }
      }
    }
    if (imgs.length > 0) {
      e.preventDefault();
      addFiles(imgs);
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) addFiles(dropped);
  }

  function onFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const arr = Array.from(e.target.files || []);
    if (arr.length > 0) addFiles(arr);
    e.target.value = '';
  }

  function removeFile(i: number) {
    setFiles(prev => prev.filter((_, idx) => idx !== i));
  }

  async function send() {
    const c = content.trim();
    if (!c && files.length === 0) return;
    setSending(true);
    try {
      const r = await todosApi.postComment(todoId, c, files);
      if (r.success && r.data) {
        setComments(prev => {
          const next = [...prev, r.data!];
          onCountChangeRef.current?.(next.length);
          return next;
        });
        setContent(''); setFiles([]);
      } else onToast?.(r.error || '댓글 작성 실패', 'error');
    } catch (e) { onToast?.(e instanceof Error ? e.message : '실패', 'error'); }
    finally { setSending(false); }
  }

  async function remove(commentId: string) {
    if (!window.confirm('이 댓글을 삭제하시겠습니까?')) return;
    try {
      const r = await todosApi.deleteComment(todoId, commentId);
      if (r.success) {
        setComments(prev => {
          const next = prev.filter(c => c.id !== commentId);
          onCountChangeRef.current?.(next.length);
          return next;
        });
        onToast?.('삭제됨', 'success');
      } else onToast?.(r.error || '삭제 실패', 'error');
    } catch (e) { onToast?.(e instanceof Error ? e.message : '실패', 'error'); }
  }

  async function openAttachment(commentId: string, attId: string) {
    // 사용자 클릭 시점에 미리 새 탭을 열어둠 (브라우저 팝업 차단 회피)
    const w = window.open('about:blank', '_blank');
    try {
      const r = await todosApi.getCommentAttachmentUrl(todoId, commentId, attId);
      if (r.success && r.data?.url) {
        if (w) w.location.href = r.data.url;
        else window.location.href = r.data.url;
      } else {
        if (w) w.close();
        onToast?.(r.error || '다운로드 실패', 'error');
      }
    } catch (e) {
      if (w) w.close();
      onToast?.(e instanceof Error ? e.message : '다운로드 실패', 'error');
    }
  }

  // 댓글 수정
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  function startEdit(c: TodoComment) {
    setEditingId(c.id);
    setEditingText(c.content);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingText('');
  }

  async function saveEdit() {
    if (!editingId) return;
    const text = editingText.trim();
    if (!text) { onToast?.('내용을 입력하세요', 'error'); return; }
    try {
      const r = await todosApi.editComment(todoId, editingId, text);
      if (r.success && r.data) {
        setComments(prev => prev.map(c => c.id === editingId ? r.data! : c));
        setEditingId(null); setEditingText('');
        onToast?.('수정됨', 'success');
      } else onToast?.(r.error || '수정 실패', 'error');
    } catch (e) { onToast?.(e instanceof Error ? e.message : '실패', 'error'); }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  }

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub,
        marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
        💬 댓글 {comments.length > 0 && <span style={{ color: C.accent }}>({comments.length})</span>}
      </div>

      {/* 댓글 리스트 */}
      <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 10 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 12, fontSize: T.xs, color: C.muted }}>⏳ 로딩...</div>
        ) : comments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '10px 12px', fontSize: T.xs, color: C.muted,
            fontStyle: 'italic' }}>
            첫 댓글을 남겨보세요
          </div>
        ) : (
          comments.map(c => {
            const isMine = c.author_id === myId;
            const isAuthorCreator = c.author_id === creatorId;
            const canDelete = isMine || myId === creatorId;
            const initial = (c.author_name || '?').charAt(0);
            return (
              <div key={c.id} style={{ display: 'flex', gap: 9, marginBottom: 13 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isMine ? C.text : (isAuthorCreator ? C.accentLt : C.orangeLt),
                  color: isMine ? C.white : (isAuthorCreator ? C.accent : C.orange),
                  fontSize: 11, fontWeight: 700, flexShrink: 0,
                }}>
                  {initial}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5,
                    fontSize: T.xs, color: C.muted, marginBottom: 2 }}>
                    <strong style={{ color: C.text, fontSize: T.xs, fontWeight: 700 }}>
                      {c.author_name || '알 수 없음'}{isMine ? ' (나)' : ''}
                    </strong>
                    <span style={{
                      padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700,
                      background: isAuthorCreator ? C.accentLt : C.orangeLt,
                      color: isAuthorCreator ? C.accent : C.orange,
                    }}>{isAuthorCreator ? '의뢰자' : '담당자'}</span>
                    <span>{fmtDateTime(c.created_at)}</span>
                    {c.updated_at !== c.created_at && (
                      <span style={{ fontSize: 10, color: C.muted, fontStyle: 'italic' }}>(수정됨)</span>
                    )}
                    {isMine && editingId !== c.id && (
                      <button onClick={() => startEdit(c)}
                        style={{ marginLeft: 'auto', border: 'none', background: 'transparent',
                          color: C.muted, fontSize: T.xs, cursor: 'pointer', padding: '2px 4px' }}>
                        ✏ 수정
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => remove(c.id)}
                        style={{ marginLeft: isMine && editingId !== c.id ? 0 : 'auto', border: 'none', background: 'transparent',
                          color: C.muted, fontSize: T.xs, cursor: 'pointer', padding: '2px 4px' }}>
                        🗑
                      </button>
                    )}
                  </div>
                  {editingId === c.id ? (
                    <div style={{ marginTop: 4 }}>
                      <textarea value={editingText} onChange={e => setEditingText(e.target.value)}
                        autoFocus
                        style={{ width: '100%', padding: '7px 10px', borderRadius: R.sm,
                          border: `1px solid ${C.accent}`, fontSize: T.sm,
                          minHeight: 60, resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit',
                          boxSizing: 'border-box' }} />
                      <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
                        <button onClick={saveEdit}
                          style={{ padding: '4px 12px', borderRadius: R.sm, border: 'none',
                            background: C.accent, color: C.white, fontSize: T.xs, fontWeight: 700,
                            cursor: 'pointer' }}>저장</button>
                        <button onClick={cancelEdit}
                          style={{ padding: '4px 12px', borderRadius: R.sm,
                            border: `1px solid ${C.border}`, background: C.white,
                            color: C.sub, fontSize: T.xs, cursor: 'pointer' }}>취소</button>
                      </div>
                    </div>
                  ) : c.content && (
                    <div style={{ fontSize: T.sm, lineHeight: 1.6, color: C.text,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {c.content}
                    </div>
                  )}
                  {Array.isArray(c.attachments) && c.attachments.length > 0 && (
                    <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {c.attachments.map(att => (
                        <button key={att.id} onClick={() => openAttachment(c.id, att.id)}
                          style={{ padding: '3px 9px', borderRadius: R.sm,
                            border: `1px solid ${C.accent}`, background: C.white,
                            color: C.accent, fontSize: T.xs, fontWeight: 600,
                            cursor: 'pointer', maxWidth: 220, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={`${att.fileName} (${fileSize(att.size)})`}>
                          📎 {att.fileName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 입력 영역 */}
      <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{ paddingTop: 10, borderTop: `1px solid ${C.border}`,
          background: dragOver ? C.accentLt : 'transparent',
          borderRadius: R.sm, padding: dragOver ? 10 : '10px 0 0 0',
          transition: 'background 0.15s' }}>
        {files.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
            {files.map((f, i) => (
              <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 4px 3px 9px', borderRadius: R.sm,
                border: `1px solid ${C.border}`, background: C.white, fontSize: T.xs }}>
                <span style={{ color: C.accent, fontWeight: 600 }}>📎</span>
                <span style={{ color: C.text, maxWidth: 160, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.name}>{f.name}</span>
                <span style={{ color: C.muted, fontSize: 10 }}>{fileSize(f.size)}</span>
                <button onClick={() => removeFile(i)} style={{ border: 'none',
                  background: 'transparent', color: C.muted, cursor: 'pointer',
                  fontSize: T.xs, padding: '0 4px' }}>✕</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <button onClick={() => fileInputRef.current?.click()}
            title="파일 첨부"
            style={{ padding: '7px 9px', borderRadius: R.sm,
              border: `1px solid ${C.border}`, background: C.white,
              color: C.sub, fontSize: T.sm, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              height: 36, lineHeight: 1 }}>
            📎
          </button>
          <input ref={fileInputRef} type="file" multiple onChange={onFilePick}
            style={{ display: 'none' }} />
          <textarea value={content} onChange={e => setContent(e.target.value)}
            onPaste={onPaste} onKeyDown={onKeyDown}
            placeholder="댓글 (Enter 전송, Shift+Enter 줄바꿈, 이미지 Ctrl+V/드래그 가능)"
            disabled={sending}
            rows={1}
            style={{ flex: 1, padding: '7px 10px', borderRadius: R.sm,
              border: `1px solid ${C.border}`, fontSize: T.sm, resize: 'vertical',
              height: 36, minHeight: 36, maxHeight: 120, lineHeight: 1.5,
              fontFamily: 'inherit', boxSizing: 'border-box' }} />
          <button onClick={send} disabled={sending || (!content.trim() && files.length === 0)}
            style={{ padding: '0 14px', borderRadius: R.sm, border: 'none',
              background: (sending || (!content.trim() && files.length === 0)) ? C.muted : C.accent,
              color: C.white, fontSize: T.sm, fontWeight: 700,
              cursor: sending ? 'wait' : 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              height: 36 }}>
            {sending ? '전송중' : '전송'}
          </button>
        </div>
      </div>
    </div>
  );
}
